import { Box, Button, CircularProgress, CssBaseline, Link, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import guruPortrait from '../../assets/guru-portrait-1600.webp';
import srmdLogo from '../../assets/srmd-logo-horizontal.png';
import userServiceInstance from '../../services/UserService';
import { takeLoginRedirect } from '../../utils/authUtils';

const BG1 = '#f6f1e6';
const BG2 = '#e9ddc7';
const INK = '#2c2418';
const ACCENT = '#b5541f';
const MUTED = '#71685a';
const LINE = 'rgba(44,36,24,0.14)';
const GLOW_A = 'rgba(181,84,31,0.20)';
const GLOW_B = 'rgba(60,92,82,0.16)';

function Copyright(props) {
    return (
        <Typography variant="body2" align="center" {...props} sx={{ color: MUTED, ...props.sx }}>
            {'Copyright © '}
            <Link color="inherit" href="https://www.srmd.org/">
                SRMD
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

export default function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            const response = await userServiceInstance.login(email, password);

            if (response.data.authorization === undefined) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Invalid Email Or Password!',
                    icon: 'error',
                    confirmButtonText: 'Close',
                    background: BG1,
                    color: INK,
                });
                setLoading(false);
                return;
            }

            localStorage.setItem('authToken', JSON.stringify(response.data.authorization));

            // Process language access
            let languageAccess = {};
            if (response.data.languageAccess) {
                if (typeof response.data.languageAccess === 'string') {
                    try {
                        languageAccess = JSON.parse(response.data.languageAccess);
                    } catch (error) {
                        console.error('Error parsing language access:', error);
                    }
                } else {
                    languageAccess = response.data.languageAccess;
                }
            }

            // Save user data
            const userData = {
                email: response.data.email || '',
                firstName: response.data.firstName || '',
                lastName: response.data.lastName || '',
                userType: response.data.userType || 'user',
                access: response.data.accessToken,
                roles: response.data.roles || [],
                languageAccess: languageAccess || {},
            };

            localStorage.setItem('authUser', JSON.stringify(userData));
            window.location.href = takeLoginRedirect() || '/';
        } catch (error) {
            console.error('Login error:', error);
            Swal.fire({
                title: 'Error',
                text: 'An error occurred during login. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                background: BG1,
                color: INK,
            });
            setLoading(false);
        }
    };

    return (
        <Box sx={{ background: BG1, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CssBaseline />

            {/* Hero */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1.15fr' },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: '#241d13',
                        minHeight: { xs: 280, md: 'auto' },
                    }}
                >
                    <Box
                        component="img"
                        src={guruPortrait}
                        alt="Pujya Gurudevshri Rakeshbhai"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: '50% 18%',
                            filter: 'saturate(1.14) contrast(1.05)',
                            display: 'block',
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        px: { xs: 3, sm: 4, md: 7 },
                        py: { xs: 6, md: 0 },
                        background: `
                            radial-gradient(ellipse 780px 460px at 18% 0%, ${GLOW_A}, transparent 62%),
                            radial-gradient(ellipse 900px 520px at 88% 30%, ${GLOW_B}, transparent 65%),
                            linear-gradient(180deg, ${BG1} 0%, ${BG2} 100%)
                        `,
                    }}
                >
                    <Box
                        component="img"
                        src={srmdLogo}
                        alt="Shrimad Rajchandra Mission Dharampur"
                        sx={{
                            height: { xs: 46, sm: 54 },
                            width: 'auto',
                            alignSelf: 'flex-start',
                            display: 'block',
                            mb: 4,
                            transform: 'translateY(-48px)',
                        }}
                    />

                    <Typography
                        component="h1"
                        onClick={() => navigate('/')}
                        sx={{
                            fontFamily: '"Marcellus", serif',
                            fontWeight: 400,
                            color: INK,
                            fontSize: { xs: '2.4rem', sm: '2.8rem', md: '3.1rem' },
                            lineHeight: 1.08,
                            mb: 4,
                            cursor: 'pointer',
                            width: 'fit-content',
                        }}
                    >
                        AmrutVaani
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 340 }}>
                        <TextField
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            variant="standard"
                            InputLabelProps={{
                                sx: {
                                    color: MUTED,
                                    fontFamily: '"Karla", sans-serif',
                                    fontSize: '1.37rem',
                                    '&.Mui-focused': { color: ACCENT },
                                },
                            }}
                            sx={{
                                mt: 3.5,
                                input: { color: INK, fontFamily: '"Karla", sans-serif', pt: 2.5 },
                                '& .MuiInput-underline:before': { borderBottomColor: LINE },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                    borderBottomColor: `${INK}33`,
                                },
                                '& .MuiInput-underline:after': { borderBottomColor: ACCENT },
                            }}
                        />
                        <TextField
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            variant="standard"
                            InputLabelProps={{
                                sx: {
                                    color: MUTED,
                                    fontFamily: '"Karla", sans-serif',
                                    fontSize: '1.37rem',
                                    '&.Mui-focused': { color: ACCENT },
                                },
                            }}
                            sx={{
                                mt: 3.5,
                                input: { color: INK, fontFamily: '"Karla", sans-serif', pt: 2.5 },
                                '& .MuiInput-underline:before': { borderBottomColor: LINE },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                    borderBottomColor: `${INK}33`,
                                },
                                '& .MuiInput-underline:after': { borderBottomColor: ACCENT },
                            }}
                        />

                        <Button
                            disabled={email === '' || password === '' || loading}
                            type="submit"
                            fullWidth
                            variant="outlined"
                            sx={{
                                mt: 4,
                                py: 1.3,
                                borderRadius: 999,
                                fontFamily: '"Karla", sans-serif',
                                fontWeight: 600,
                                letterSpacing: '0.03em',
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                color: INK,
                                borderColor: ACCENT,
                                borderWidth: 1.5,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: `${ACCENT}1f`,
                                    borderColor: ACCENT,
                                },
                                '&.Mui-disabled': {
                                    color: `${MUTED}99`,
                                    borderColor: LINE,
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            {loading ? <CircularProgress size={22} sx={{ color: ACCENT }} /> : 'Sign In'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ background: BG1, py: 3 }}>
                <Copyright />
            </Box>
        </Box>
    );
}
